import { AuditEntryDto, PropertyChangeDto } from "api/funnel";
import { FC, useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { funnelsAPI } from "services/swagger-api";
import { History, ChevronDown, ChevronRight, ArrowLeft, CheckCircle, Edit, Trash2, Clock, Monitor, Tag, type LucideIcon } from "lucide-react";

const PAGE_SIZE = 50;

const ChangeTypeBadge: FC<{ changeType?: string }> = ({ changeType }) => {
  const config: Record<string, { bg: string; text: string; border: string; label: string; icon: LucideIcon }> = {
    Created: { bg: "bg-green-100", text: "text-green-800", border: "border-green-200", label: "Création", icon: CheckCircle },
    Updated: { bg: "bg-blue-100", text: "text-blue-800", border: "border-blue-200", label: "Modification", icon: Edit },
    Deleted: { bg: "bg-red-100", text: "text-red-800", border: "border-red-200", label: "Suppression", icon: Trash2 },
  };
  const c = config[changeType ?? ""] ?? { bg: "bg-gray-100", text: "text-gray-800", border: "border-gray-200", label: changeType ?? "—", icon: Edit };
  const Icon = c.icon;
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${c.bg} ${c.text} border ${c.border} inline-flex items-center gap-1`}>
      <Icon size={12} />
      {c.label}
    </span>
  );
};


const PropertyChangesTable: FC<{ changes: PropertyChangeDto[] }> = ({ changes }) => (
  <div className="bg-gray-50 rounded-xl p-4 mt-2">
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wide border-b border-gray-200">
          <th className="pb-2 pr-6">Propriété</th>
          <th className="pb-2 pr-6">Ancienne valeur</th>
          <th className="pb-2 pr-6">Nouvelle valeur</th>
          <th className="pb-2">Type</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200">
        {changes.map((change, i) => (
          <tr key={i}>
            <td className="py-2 pr-6 font-medium text-gray-900">{change.propertyName}</td>
            <td className="py-2 pr-6 text-red-600 line-through">{change.originalValue || "—"}</td>
            <td className="py-2 pr-6 text-green-600">{change.newValue || "—"}</td>
            <td className="py-2 text-gray-500 text-xs">{change.propertyType}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const AuditRow: FC<{ entry: AuditEntryDto }> = ({ entry }) => {
  const [expanded, setExpanded] = useState(false);
  const hasChanges = entry.propertyChanges && entry.propertyChanges.length > 0;

  return (
    <div>
      <div
        className={`flex items-center gap-2 px-6 py-4 ${hasChanges ? "cursor-pointer hover:bg-gray-50 transition-colors" : ""}`}
        onClick={() => hasChanges && setExpanded(!expanded)}
      >
        <div className="w-5 flex-shrink-0">
          {hasChanges && (expanded ? <ChevronDown size={16} className="text-gray-400" /> : <ChevronRight size={16} className="text-gray-400" />)}
        </div>
        <div className="w-44 flex-shrink-0">
          <div className="flex items-center gap-2">
            <Clock size={14} className="text-orange-500" />
            <span className="text-sm text-gray-900">
              {entry.changeTime ? new Date(entry.changeTime).toLocaleString("fr-FR") : "—"}
            </span>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          {entry.contextLabel && (
            <div className="flex items-center gap-2">
              <Tag size={14} className="text-primary flex-shrink-0" />
              <span className="text-sm text-gray-600 truncate">{entry.contextLabel}</span>
            </div>
          )}
        </div>
        <div className="w-32 flex-shrink-0"><ChangeTypeBadge changeType={entry.changeType} /></div>
        <div className="w-48 flex-shrink-0 text-sm text-gray-600 truncate">{entry.entityType || "—"}</div>
        <div className="w-32 flex-shrink-0">
          <div className="flex items-center gap-2">
            <Monitor size={14} className="text-gray-400" />
            <span className="text-sm text-gray-500 truncate">{entry.clientIpAddress || "—"}</span>
          </div>
        </div>
      </div>
      {expanded && hasChanges && (
        <div className="px-12 pb-4">
          <PropertyChangesTable changes={entry.propertyChanges!} />
        </div>
      )}
    </div>
  );
};

const SkeletonRows: FC = () => (
  <>
    {Array.from({ length: 5 }).map((_, i) => (
      <div key={i} className="flex items-center gap-2 px-6 py-4 animate-pulse">
        <div className="w-5 flex-shrink-0" />
        <div className="w-44 flex-shrink-0"><div className="h-4 bg-gray-200 rounded w-32" /></div>
        <div className="flex-1"><div className="h-4 bg-gray-200 rounded w-36" /></div>
        <div className="w-32 flex-shrink-0"><div className="h-4 bg-gray-200 rounded w-20" /></div>
        <div className="w-48 flex-shrink-0"><div className="h-4 bg-gray-200 rounded w-32" /></div>
        <div className="w-32 flex-shrink-0"><div className="h-4 bg-gray-200 rounded w-24" /></div>
      </div>
    ))}
  </>
);

interface TunnelAuditHistoryProps {
  /**
   * Rendu à l'intérieur de l'onglet « Historique » du détail tunnel : le titre et le
   * bouton retour sont déjà portés par la page, on ne les répète pas.
   */
  embedded?: boolean;
}

export const TunnelAuditHistory: FC<TunnelAuditHistoryProps> = ({ embedded = false }) => {
  const { tunnelId } = useParams<{ tunnelId: string }>();
  const navigate = useNavigate();
  const [entries, setEntries] = useState<AuditEntryDto[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAuditHistory = useCallback(async (pageNumber: number) => {
    if (!tunnelId) return;
    try {
      setIsLoading(true);
      setError(null);
      const data = await funnelsAPI.getTunnelAuditHistory({
        tunnelId,
        pageNumber,
        pageSize: PAGE_SIZE,
      });
      setEntries(data.entries ?? []);
      setTotalCount(data.totalCount ?? 0);
    } catch (err) {
      console.error("Error fetching audit history:", err);
      setError("Impossible de charger l'historique d'audit");
    } finally {
      setIsLoading(false);
    }
  }, [tunnelId]);

  useEffect(() => {
    fetchAuditHistory(page);
  }, [fetchAuditHistory, page]);

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  return (
    <section className="w-full">
      {/* Title */}
      {!embedded && (
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate("/back-office/tunnels")}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <ArrowLeft size={20} className="text-gray-500" />
          </button>
          <History className="text-primary w-7" />
          <h2 className="text-3xl font-semibold text-zinc-800">Historique d'audit</h2>
          <span className="text-sm text-gray-500 ml-2">({totalCount} entrée{totalCount > 1 ? "s" : ""})</span>
        </div>
      )}

      {/* Table */}
      <div className="overflow-hidden rounded-xl shadow-lg">
        {/* Header */}
        <div className="flex items-center gap-2 px-6 py-3 bg-primary text-white text-xs font-medium tracking-wider">
          <div className="w-5 flex-shrink-0" />
          <div className="w-44 flex-shrink-0">DATE</div>
          <div className="flex-1">CONTEXTE</div>
          <div className="w-32 flex-shrink-0">ACTION</div>
          <div className="w-48 flex-shrink-0">ENTITÉ</div>
          <div className="w-32 flex-shrink-0">IP</div>
        </div>

        {/* Content */}
        <div className="bg-white divide-y divide-gray-200">
          {isLoading ? (
            <SkeletonRows />
          ) : error ? (
            <div className="flex items-center justify-center px-6 py-12">
              <p className="text-red-500">{error}</p>
            </div>
          ) : entries.length === 0 ? (
            <div className="flex items-center justify-center px-6 py-12">
              <p className="text-gray-500">Aucun historique d'audit</p>
            </div>
          ) : (
            entries.map((entry, i) => <AuditRow key={i} entry={entry} />)
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-3 bg-white border-t border-gray-200">
            <span className="text-sm text-gray-600">
              Page {page} sur {totalPages} — {totalCount} résultat{totalCount > 1 ? "s" : ""}
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="w-9 h-9 flex items-center justify-center rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                &lsaquo;
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const start = Math.max(1, Math.min(page - 2, totalPages - 4));
                const pageNum = start + i;
                if (pageNum > totalPages) return null;
                return (
                  <button
                    key={pageNum}
                    onClick={() => setPage(pageNum)}
                    className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm font-medium transition-all hover:scale-105 ${
                      pageNum === page
                        ? "bg-primary text-white shadow-lg shadow-primary/20"
                        : "text-gray-600 hover:bg-gray-100 hover:text-primary"
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="w-9 h-9 flex items-center justify-center rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                &rsaquo;
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
